import { ApplicationElement, html } from '../application_element/element.js';
import { ref, createRef } from 'lit/directives/ref.js';
import style from './style.lit.css';
import '../chat_form/element.js';
import '../chat_message_new/element.js';
import '../chat_form_reply_preview/element.js';
import '../chat_messages_list/element.js';

import ServerTime from '../../lib/server_time.js';

import debounce from 'lodash-es/debounce.js';

import { ChatController } from './lib/chat.controller.js';

import translations from './translations.js';

import store from 'store2';

/**
 * @prop kind - Type of chat (options: direct, group, multi)
 */
export default class Chat extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static translations = translations;

  static properties = {
    chatId: { type: String },
    chatUrl: { type: String },
    publicKey: { type: Object },
    signature: { type: String },
    channelName: { type: String },
    membersCount: { type: Number },
    kind: { type: String },
    currentUser: { type: Object },
    isMobileDevice: { type: Boolean },

    // States
    messageForEdit: { state: true },
    messageForReply: { state: true },
    scrollIndicatorVisible: { state: true },
    scrollIndicatorIntersected: { state: true },
  };

  static pusher = null;

  perPage = 10;

  chatController = new ChatController(this);

  formRef = createRef();

  listRef = createRef();

  debouncedStayInSync = debounce(
    () => this.chatController.loadMessages({ showLoader: false }),
    5000
  );

  constructor() {
    super();
    this.scrollIndicatorVisible = true;
    this.kind = 'direct';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('visibilitychange', this);
    document.addEventListener('keydown', this);

    this.dispatchEvent(
      new Event('chat-activated', { bubbles: true, composed: true })
    );

    this.messageForReply = store.get(this.replyStoreKey);
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this);

    document.dispatchEvent(
      new Event('chat-deactivated', { bubbles: true, composed: true })
    );

    super.disconnectedCallback();
  }

  get computed() {
    return this.chatController?.value || { newMessages: [], sorted: [] };
  }

  get form() {
    return this.formRef.value;
  }

  get list() {
    return this.listRef.value;
  }

  /** @internal */
  get isIosDevice() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  /** @internal */
  get replyStoreKey() {
    return `chat-reply-${this.chatId}`;
  }

  getMessage(messageId) {
    return this.chatController.chatModel.getMessage(messageId);
  }

  resetScrollIndicatorVisibility() {
    this.scrollIndicatorVisible = true;
  }

  handleEvent(e) {
    e.type === 'visibilitychange' && this.onVisibilityChange(e);
    e.type === 'keydown' && this.onTextareaKeyDown(e);
    e.type === 'resize' && this.onResizeHandler(e);
  }

  enterEditMode(message) {
    if (this.messageForReply) this.exitReplyMode();
    this.messageForEdit = message;
  }

  exitEditMode() {
    this.messageForEdit = null;
  }

  enterReplyMode(message) {
    if (this.messageForEdit) this.exitEditMode();
    this.messageForReply = message;
    store.set(this.replyStoreKey, message);
  }

  exitReplyMode() {
    this.messageForReply = null;
    store.remove(this.replyStoreKey);
  }

  onTextareaKeyDown({ key }) {
    if (key === 'Escape') {
      if (this.messageForEdit) this.exitEditMode();
      if (this.messageForReply) this.exitReplyMode();
    }
  }

  handleArrowUp() {
    const formIsEmpty = this.form?.value.length === 0;

    if (formIsEmpty) {
      const { isMyMessageLast, myLastWrittenMessage } = this.computed;

      isMyMessageLast && this.enterEditMode(myLastWrittenMessage);
    }
  }

  onFormKeyDown({ key }) {
    if (key == 'ArrowUp') {
      this.handleArrowUp();
    }
  }

  onVisibilityChange() {
    document.visibilityState === 'visible' &&
      this.chatController.loadMessages();
  }

  dismissNewMessagesNotification() {
    this.lastMessageReceivedAwayFromBottomAt = null;
  }

  setupNewMessagesNotification() {
    if (!this.list?.scrollNearBottom) {
      this.lastMessageReceivedAwayFromBottomAt ??= ServerTime.toJSON();
    } else {
      this.dismissNewMessagesNotification();
    }
  }

  prepareParentParams(params) {
    return { ...params, parent: this.messageForReply };
  }

  editMessage({ value: body, files }) {
    const params = {
      uuid: this.messageForEdit.uuid,
      body,
      files,
      parent: this.messageForEdit.parent,
    };

    this.chatController.edit(params);

    this.messageForEdit = null;
  }

  createMessage(params) {
    // We need to dismiss before createMessage
    this.dismissNewMessagesNotification();

    this.list.forcedScrollToEnd = true;
    this.chatController.createMessage(
      this.messageForReply ? this.prepareParentParams(params) : params
    );

    if (this.messageForReply) this.exitReplyMode();
    // Backup method for syncing messages
    this.debouncedStayInSync();
  }

  onFormSubmit(e) {
    e.preventDefault();

    const { safeValue, files } = e.target;
    const params = { value: safeValue, files };

    if (this.messageForEdit) {
      this.editMessage(params);
    } else {
      this.createMessage(params);
    }
  }

  get showUserName() {
    return this.kind !== 'direct';
  }

  // NOTE: We are using our ChatMessageEvent
  onMessageDelete(e) {
    this.exitEditMode();
    this.chatController.delete({
      uuid: e.realTarget.uuid,
      deletedByUserName: this.currentUser.name,
    });
  }

  onMessageRestore(e) {
    this.exitEditMode();
    this.chatController.restore(e.realTarget.uuid);
  }

  onMessageEdit(e) {
    const message = this.getMessage(e.realTarget.uuid);

    this.enterEditMode(message);
  }

  removeMessage(uuid) {
    this.chatController.remove(uuid);
  }

  onMessageRetry(e) {
    this.chatController.retry(e.realTarget.uuid);
  }

  onMessageDismiss(e) {
    this.removeMessage(e.realTarget.uuid);
  }

  onMessageSeen(e) {
    const messageElement = e.realTarget;
    this.chatController.update({
      uuid: messageElement.uuid,
      // seenAt: ServerTime.toJSON(),
    });
  }

  onMessageReply(e) {
    const element = e.realTarget;
    const message = this.getMessage(element.uuid);
    this.enterReplyMode(message);
  }

  get allMessagesRead() {
    return !this.computed.hasUnreadMessages;
  }

  loadMoreMessages() {
    const { first, noMoreMessages, loading } = this.computed;
    if (!first || noMoreMessages || loading) return;

    this.chatController.loadMessages({
      query: { before: first.createdAt },
    });
  }

  get showScrollIndicator() {
    return (
      !this.scrollIndicatorIntersected &&
      this.scrollIndicatorPosition <= 0 &&
      this.scrollIndicatorVisible &&
      this.computed.newMessages.length
    );
  }

  onNewMessagesSeparatorIntersected(e) {
    this.scrollIndicatorIntersected = e.target.newMessagesSeparatorVisible;
    this.scrollIndicatorPosition = e.target.newMessagesSeparatorPosition;
  }

  markAllAsRead() {
    this.chatController.markAllAsRead();
  }

  markChatAsRead() {
    return this.chatController.markChatAsRead();
  }

  onToggleMute() {
    this.chatController.toggleMute();
  }

  onResizeHandler() {
    const pageOffset = this.initViewPortHeight - window.visualViewport.height;

    window.scrollTo({ top: pageOffset, behavior: 'instant' });
  }

  onFormFocus() {
    if (!window.visualViewport) {
      return;
    }

    /*
      On iOS 16.5. keyboard sometimes goes over the WebView content,
      therefore we force window to scroll to the end when keyboard is opened (focus event)
    */

    if (this.isIosDevice) {
      this.initViewPortHeight = window.visualViewport.height;

      window.visualViewport.addEventListener('resize', this);
    }
  }

  onFormBlur() {
    window.visualViewport.removeEventListener('resize', this);
  }

  render() {
    return html`
      <slot name="actions">
        <div>
          <button @click=${this.onToggleMute}>
            ${this.computed.isMuted ? this.t('unmute') : this.t('mute')}
          </button>
        </div>
        <div>
          <button
            type="button"
            @click=${this.markAllAsRead}
            ?disabled=${this.allMessagesRead}>
            ${this.t('mark_all_as_read')}
          </button>
        </div>
      </slot>

      <ef-chat-messages-list
        ${ref(this.listRef)}
        @scroller-near-top=${this.loadMoreMessages}
        @message-delete=${this.onMessageDelete}
        @message-restore=${this.onMessageRestore}
        @message-edit=${this.onMessageEdit}
        @message-retry=${this.onMessageRetry}
        @message-dismiss=${this.onMessageDismiss}
        @message-seen=${this.onMessageSeen}
        @message-exit-edit-mode=${this.exitEditMode}
        @new-messages-separator-intersected=${this
          .onNewMessagesSeparatorIntersected}
        @message-reply=${this.onMessageReply}
        .noMoreMessages=${this.computed.noMoreMessages}
        .chat=${this}
        .adminIds=${this.computed.adminIds}
        .messageForEdit=${this.messageForEdit}
        .firstNewMessage=${this.computed.newMessages[0]}
        .newMessagesCount=${this.computed.newMessages.length}
        .loading=${this.computed?.loading}
        .showScrollIndicator=${this.showScrollIndicator}
        .messages=${this.computed.sorted}
        .unreadMessagesToLoadCount=${this.computed.unreadMessagesToLoadCount}>
      </ef-chat-messages-list>

      ${this.messageForReply
        ? html`
            <ef-chat-form-reply-preview
              @chat-form-reply-preview-exit=${this.exitReplyMode}
              .message=${this.messageForReply}
              my=${this.messageForReply.userId === this.currentUser.id}>
            </ef-chat-form-reply-preview>
          `
        : null}

      <ef-chat-form
        ${ref(this.formRef)}
        @focus=${this.onFormFocus}
        @keydown=${this.onFormKeyDown}
        @blur=${this.onFormBlur}
        @chat-form-exit-edit-mode=${this.exitEditMode}
        @message-create=${this.onFormSubmit}
        .isMobileDevice=${this.isMobileDevice}
        .isEdit=${!!this.messageForEdit}
        .safeValue=${this.messageForEdit?.body || ''}
        .chatId=${this.chatId}>
        <slot slot="textarea" name="textarea"></slot>
      </ef-chat-form>
    `;
  }
}

window.Chat = Chat;

Chat.registerTagName('ef-chat');

customElements.get('ef-chat') || customElements.define('ef-chat', Chat);
